import { computed, type Ref, type ComputedRef } from 'vue'
import {
  EMOJI_PRIORITY,
  iconGridLayout,
  getWeatherIcon,
  getWeatherEmoji,
  getWeatherIconDescription,
  isDayTime,
  getTimeLabel
} from '../utils/weatherIcons'
import { type TableRow } from '../utils/csvFormatting'
import { type ForecastData } from './useWeatherData'

export { getWeatherIconDescription }

export interface IconCellData {
  icon: string
  temp: string
  humidity: string
  wind: string
  windDir: string
  timeLabel: string
  emoji: string
  hasData: boolean
}

export function getIconDataForTime(
  timeStr: string,
  dayIndex: number,
  tableDataForDay: TableRow[],
  forecastsArray: ForecastData[],
  locationDates: Date[]
): IconCellData {
  const timeData = tableDataForDay.find(row => row.time === timeStr)

  if (!timeData) {
    return { icon: '24', temp: '-', humidity: '-', wind: '-', windDir: '', timeLabel: getTimeLabel(timeStr), emoji: '', hasData: false }
  }

  const metadata = forecastsArray[dayIndex]?.metadata
  const latitude = metadata?.latitude
  const longitude = metadata?.longitude
  const sunrise = metadata?.sunrise
  const sunset = metadata?.sunset
  const forecastDate = locationDates[dayIndex]

  const precipitation = Math.round(parseFloat(timeData.rain) || 0)
  const cloudCover = Math.round(parseFloat(timeData.cloud?.split('/')[0]) || 0)

  let wind = 0
  if (timeData.windSpeed) {
    wind = Math.round(parseFloat(timeData.windSpeed) || 0)
  } else if (timeData.wind) {
    const parts = timeData.wind.toString().split(' ')
    wind = Math.round(parseFloat(parts[parts.length - 1]) || 0)
  }

  const hour = parseInt(timeStr.split(':')[0], 10)
  let isDay = true
  try {
    isDay = isDayTime(hour, forecastDate, latitude, longitude, sunrise, sunset)
  } catch {
    // default to day
  }

  const icon = getWeatherIcon(precipitation, cloudCover, wind, isDay)
  const emoji = getWeatherEmoji(precipitation, wind, parseFloat(timeData.temp) || 0, timeStr, parseFloat(timeData.humidity) || 0, forecastDate, latitude, longitude)

  return {
    icon: icon.padStart(2, '0'),
    temp: timeData.temp || '-',
    humidity: timeData.humidity || '-',
    wind: timeData.windDir ? `${timeData.windDir} ${timeData.wind}` : (timeData.wind || '-'),
    windDir: timeData.windDir || '',
    timeLabel: getTimeLabel(timeStr),
    emoji,
    hasData: true
  }
}

interface IconGridOptions {
  forecastsArray: Ref<ForecastData[]>
  formattedTableDataArray: ComputedRef<TableRow[][]>
  locationDates: ComputedRef<Date[]>
  activeTab: Ref<number>
}

export function useIconGrid(opts: IconGridOptions) {
  const getIconData = (timeStr: string, dayIndex: number): IconCellData => {
    return getIconDataForTime(
      timeStr,
      dayIndex,
      opts.formattedTableDataArray.value[dayIndex] || [],
      opts.forecastsArray.value,
      opts.locationDates.value
    )
  }

  const usedEmojisForDay = computed(() => {
    const dayIndex = opts.activeTab.value
    const usedEmojis = new Set<string>()
    for (const row of iconGridLayout) {
      for (const timeStr of row) {
        const iconData = getIconData(timeStr, dayIndex)
        if (iconData.hasData && iconData.emoji) {
          for (const emojiItem of EMOJI_PRIORITY) {
            if (iconData.emoji.includes(emojiItem.emoji)) {
              usedEmojis.add(emojiItem.emoji)
            }
          }
        }
      }
    }
    return EMOJI_PRIORITY.filter(item => usedEmojis.has(item.emoji)).slice(0, 6)
  })

  return { getIconData, usedEmojisForDay }
}
